import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../utils/api';
import { BsSearch, BsGear, BsPlusLg, BsArrowRepeat, BsChevronLeft, BsPaperclip, BsFillSendFill, BsEmojiSmile, BsX, BsCheck2, BsCheck2All, BsClock, BsExclamationCircle, BsChatDots, BsDownload } from 'react-icons/bs';
import useWhatsappViewport from '../hooks/useWhatsappViewport';
import { dayKeyOf, dayLabelOf, listTimeOf } from '../utils/whatsappDates';
import { FREQUENT_EMOJIS } from '../pages/whatsappEmojis';
import '../pages/WhatsappPage.css';
import './WhatsappQrPanel.css';

const labels = { disconnected: 'Desconectado', connecting: 'Conectando…', qr: 'Escanea el QR', connected: 'Conectado', reconnecting: 'Reconectando…', error: 'Requiere reconexión' };
const connectionReasons = {
  waiting_for_previous_instance: 'El servidor está recuperando la sesión tras un reinicio. Se conectará automáticamente al quedar disponible.',
  database_connection_lost: 'Se interrumpió la conexión del servidor. Estamos recuperando la sesión automáticamente.',
  connection_attempt_failed: 'El intento de conexión no se completó. Se volverá a intentar automáticamente.',
  persistence_failed: 'Se está recuperando la sesión guardada. La reconexión es automática.',
  whatsapp_connection_lost: 'WhatsApp interrumpió la conexión. Estamos reconectando automáticamente.',
  logged_out: 'WhatsApp ha cerrado la sesión vinculada. Conecta el número y escanea un nuevo QR.',
  connection_replaced: 'WhatsApp ha sustituido esta conexión por otra. Revisa los dispositivos vinculados antes de reconectar.',
  session_rejected: 'WhatsApp ha rechazado la sesión. Revisa la vinculación y vuelve a conectar el número.'
};
const delivery = { sending: 'Pendiente de confirmar', sent: 'Enviado', delivered: 'Entregado', read: 'Leído', unknown: 'Envío sin confirmar' };
// Un «@lid» es un contacto cuyo teléfono WhatsApp aún no ha revelado; se traduce solo en cuanto se conoce.
const phoneLabel = (jid = '') => jid.endsWith('@lid') ? `Contacto sin número (${jid.split('@')[0]})` : `+${jid.split('@')[0]}`;
const MAX_FILE = 20 * 1024 * 1024;
const initials = (name = '') => name.trim().split(/\s+/).slice(0, 2).map((part) => part[0]).join('').toUpperCase() || '?';
const timeOf = (value) => value && !Number.isNaN(new Date(value).getTime()) ? new Date(value).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) : '';

function DeliveryStatus({ status }) {
  const Icon = ['delivered', 'read'].includes(status) ? BsCheck2All : status === 'sent' ? BsCheck2 : status === 'sending' ? BsClock : BsExclamationCircle;
  return <span className={status === 'read' ? 'tick-read' : ''} aria-label={delivery[status] || 'Envío sin confirmar'} title={delivery[status] || 'Envío sin confirmar'}><Icon aria-hidden="true" /></span>;
}

function QrAttachment({ message, onError }) {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const objectUrl = useRef('');
  const controller = useRef(null);
  useEffect(() => () => { controller.current?.abort(); if (objectUrl.current) URL.revokeObjectURL(objectUrl.current); }, []);
  async function load() {
    setLoading(true);
    controller.current = new AbortController();
    try {
      const response = await apiFetch(`/api/whatsapp/qr/media/${message.id}`, {
        signal: controller.current.signal, headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (!response.ok) throw new Error('No se pudo descargar el adjunto');
      const blob = await response.blob();
      if (controller.current.signal.aborted) return;
      objectUrl.current = URL.createObjectURL(blob); setUrl(objectUrl.current);
    } catch (err) { if (err.name !== 'AbortError') onError(err.message); }
    finally { if (!controller.current?.signal.aborted) setLoading(false); }
  }
  if (message.media_status !== 'ready') {
    const label = { pending: 'Descargando adjunto…', too_large: 'Archivo de más de 20 MB: abrir en WhatsApp', failed: 'No se pudo descargar: abrir en WhatsApp', view_once: 'Visualización única: abrir en WhatsApp' }[message.media_status];
    return label ? <small className="d-block">{label}</small> : null;
  }
  return <div className="qr-attachment mt-2">
    {!url ? <button type="button" className="btn btn-sm btn-light" disabled={loading} onClick={load}>{loading ? 'Cargando…' : `Abrir ${message.media_name || 'adjunto'}`}</button> : <>
      {['audio/ogg', 'audio/mpeg', 'audio/mp4', 'audio/wav', 'audio/webm'].includes(message.media_mime) && <audio controls preload="metadata" src={url} style={{ maxWidth: '100%' }} />}
      {['image/png', 'image/jpeg', 'image/webp'].includes(message.media_mime) && <img src={url} alt={message.media_name || 'Imagen adjunta'} style={{ maxWidth: '100%', maxHeight: 260 }} />}
      {message.media_mime === 'video/mp4' && <video controls preload="metadata" src={url} style={{ maxWidth: '100%', maxHeight: 260 }} />}
      <a className="btn btn-sm btn-light d-block mt-1" href={url} download={message.media_name || 'adjunto'}><BsDownload aria-hidden="true" /> Descargar {message.media_name || 'adjunto'}</a>
    </>}
  </div>;
}

export default function WhatsappQrPanel() {
  const [state, setState] = useState(null);
  const [peer, setPeer] = useState('');
  const [phone, setPhone] = useState('');
  const [drafts, setDrafts] = useState({});
  const [attachments, setAttachments] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmUnlink, setConfirmUnlink] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 900);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [search, setSearch] = useState('');
  const [newChatOpen, setNewChatOpen] = useState(false);
  const [connectionOpen, setConnectionOpen] = useState(false);
  const [showEmojis, setShowEmojis] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const request = useRef(null);
  const messagesRef = useRef(null);
  const nearBottomRef = useRef(true);
  const composerRef = useRef(null);
  const fileInputRef = useRef(null);
  const selection = useRef(peer);
  selection.current = peer;
  const draftKey = `${state?.account || ''}:${peer}`;
  const text = drafts[draftKey] || '';
  const file = attachments[draftKey] || null;

  useWhatsappViewport({ chatOpen: isMobile && !sidebarOpen, messagesRef, nearBottomRef });
  useEffect(() => {
    const resize = () => {
      setIsMobile(window.innerWidth < 900);
      if (window.innerWidth >= 900) setSidebarOpen(true);
    };
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  useEffect(() => {
    nearBottomRef.current = true;
    setState((old) => old ? { ...old, messages: [] } : old);
  }, [peer]);

  const call = useCallback(async (path, body, signal) => {
    const response = await apiFetch(`/api/whatsapp/qr/${path}`, {
      method: body === undefined ? 'GET' : 'POST', signal,
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, ...(body === undefined || body instanceof FormData ? {} : { 'Content-Type': 'application/json' }) },
      ...(body === undefined ? {} : { body: body instanceof FormData ? body : JSON.stringify(body) })
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'No se pudo completar la operación');
    return data;
  }, []);

  useEffect(() => {
    let active = true;
    let timer;
    const controller = new AbortController();
    setLoading(true);
    async function refresh() {
      try {
        const data = await call(`state?peer=${encodeURIComponent(peer)}`, undefined, controller.signal);
        if (active) setState(data);
      } catch (err) { if (active && err.name !== 'AbortError') setError(err.message); }
      finally { if (active) setLoading(false); }
      if (active) timer = setTimeout(refresh, 5000);
    }
    refresh();
    return () => { active = false; controller.abort(); clearTimeout(timer); };
  }, [peer, call, refreshKey]);

  useEffect(() => {
    const container = messagesRef.current;
    if (container && nearBottomRef.current) container.scrollTop = container.scrollHeight;
  }, [state?.messages, peer, sidebarOpen]);

  useEffect(() => {
    const input = composerRef.current;
    if (input) { input.style.height = 'auto'; input.style.height = `${Math.min(input.scrollHeight, 120)}px`; }
  }, [text, peer, sidebarOpen]);

  async function manage(action) {
    setBusy(true); setError('');
    try {
      const result = await call(action, {});
      setState((old) => ({ ...old, ...result, ...(action === 'disconnect' ? { messages: [], chats: [] } : {}) }));
      if (action === 'disconnect') { setPeer(''); setSidebarOpen(true); setConfirmUnlink(false); }
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }

  async function send(event) {
    event.preventDefault();
    if ((!text.trim() && !file) || busy || !peer || state?.status !== 'connected') return;
    if (file && text.length > 1024) { setError('El texto con adjunto admite hasta 1024 caracteres'); return; }
    setBusy(true); setError('');
    // Preserve the id across HTTP failures; a retry cannot send the same message twice.
    if (!request.current || request.current.peer !== peer || request.current.text !== text || request.current.account !== state.account || request.current.file !== file) {
      request.current = { peer, text, account: state.account, requestId: crypto.randomUUID(), file };
    }
    const sendingPeer = peer;
    try {
      const { file: attachment, ...payload } = request.current;
      let body = payload;
      if (attachment) {
        body = new FormData();
        Object.entries(payload).forEach(([key, value]) => body.append(key, value));
        body.append('file', attachment);
      }
      const result = await call(attachment ? 'send-media' : 'send', body);
      if (!['sent', 'delivered', 'read'].includes(result.status)) throw new Error('Envío sin confirmar. Comprueba el chat en WhatsApp antes de intentar otro envío.');
      setDrafts((old) => ({ ...old, [draftKey]: '' }));
      setAttachments((old) => ({ ...old, [draftKey]: null }));
      request.current = null;
      nearBottomRef.current = true;
      const data = await call(`state?peer=${encodeURIComponent(sendingPeer)}`);
      if (selection.current === sendingPeer) setState(data);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }

  function newChat(event) {
    event.preventDefault();
    const digits = phone.replace(/[+\s()-]/g, '').replace(/^00/, '');
    if (!/^[1-9]\d{7,14}$/.test(digits)) { setError('Introduce el teléfono con prefijo de país, por ejemplo +34 600 000 000'); return; }
    if (/^[6-9]\d{8}$/.test(digits)) { setError(`Falta el prefijo de país: escribe +34 ${digits}`); return; }
    selectChat(`${digits}@s.whatsapp.net`); setPhone('');
  }

  function selectChat(nextPeer) {
    setPeer(nextPeer); setSidebarOpen(false); setNewChatOpen(false); setShowEmojis(false); setError('');
  }

  function insertEmoji(emoji) {
    const input = composerRef.current;
    const start = input?.selectionStart ?? text.length;
    const end = input?.selectionEnd ?? text.length;
    const next = text.slice(0, start) + emoji + text.slice(end);
    if (next.length > (file ? 1024 : 4096)) return;
    setDrafts((old) => ({ ...old, [draftKey]: next }));
    requestAnimationFrame(() => { input?.focus(); input?.setSelectionRange(start + emoji.length, start + emoji.length); });
  }

  const chats = state?.chats || [];
  const selectedChat = chats.find((chat) => chat.peer === peer);
  const selectedName = selectedChat?.name || phoneLabel(peer);
  const messages = (state?.messages || []).filter((message) => message.peer === peer);
  const query = search.trim().toLocaleLowerCase('es');
  const filteredChats = chats.filter((chat) => `${chat.name || ''} ${phoneLabel(chat.peer)} ${chat.body || ''}`.toLocaleLowerCase('es').includes(query));
  const showSidebar = !isMobile || sidebarOpen;
  const showChat = !isMobile || !sidebarOpen;
  const showConnection = connectionOpen || !state || state.status !== 'connected';
  const connectionLabel = state ? labels[state.status] || state.status : 'Cargando…';
  const errorNotice = error && <div role="alert" className="qr-alert alert alert-danger">
    <span>{error}</span><button type="button" className="btn-close" aria-label="Cerrar aviso" onClick={() => setError('')} />
  </div>;

  return <section className={`whatsapp-wrap whatsapp-qr shadow-sm ${isMobile ? 'mobile' : ''}`} aria-label="WhatsApp por QR">
    {showSidebar && <aside className="whatsapp-sidebar show" aria-label="Conversaciones de WhatsApp QR">
      <div className="whatsapp-sidebar-header">
        <div className="qr-list-heading"><h2 className="fw-bold wa-list-title mb-0">WhatsApp QR</h2>
          <small role="status" className="qr-connection-status"><span className={`qr-status-dot ${state?.status === 'connected' ? 'connected' : ''}`} />{connectionLabel}</small>
        </div>
        <div className="d-flex align-items-center gap-1">
          <button type="button" className="btn btn-sm btn-light wa-header-icon" aria-label="Actualizar chats" title="Actualizar chats" disabled={loading} onClick={() => setRefreshKey((value) => value + 1)}><BsArrowRepeat className={loading ? 'spin' : ''} /></button>
          <button type="button" className="btn btn-sm btn-light wa-header-icon" aria-label="Nueva conversación" title="Nueva conversación" aria-expanded={newChatOpen} disabled={!state?.account} onClick={() => setNewChatOpen((open) => !open)}><BsPlusLg /></button>
          <button type="button" className="btn btn-sm btn-light wa-header-icon" aria-label="Conexión de WhatsApp QR" title="Conexión de WhatsApp QR" aria-expanded={showConnection} onClick={() => setConnectionOpen((open) => !open)}><BsGear /></button>
        </div>
      </div>
      <div className="qr-sidebar-scroll">
        {(!peer || (isMobile && sidebarOpen)) && errorNotice}
        {showConnection && <div className="qr-connection-panel">
          {state?.account && <p className="small mb-2">Número vinculado: <strong>{phoneLabel(state.account)}</strong></p>}
          {connectionReasons[state?.reconnection?.reason] && <p className="small mb-2" role="status">{connectionReasons[state.reconnection.reason]}</p>}
          {state?.canManage && <div className="d-flex flex-wrap gap-2 mb-2">
            <button type="button" className="btn btn-sm btn-primary" disabled={busy || !state.configured || ['connected', 'connecting', 'qr', 'reconnecting'].includes(state.status)} onClick={() => manage('connect')}>Conectar número</button>
            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={busy || !state.configured || state.status === 'disconnected'} onClick={() => setConfirmUnlink(true)}>Desvincular</button>
          </div>}
          {state && !state.configured && <p className="small">La conexión QR está pendiente de configurar en el servidor. Un administrador debe habilitar el almacenamiento seguro de la sesión.</p>}
          {state && !state.canManage && state.status === 'disconnected' && <p className="small">Un administrador debe conectar el número.</p>}
          {confirmUnlink && <div className="alert alert-warning small mb-2">¿Desvincular este número? Tendrás que escanear un nuevo QR para volver a conectarlo. El historial guardado se conserva.
            <div className="mt-2 d-flex flex-wrap gap-2"><button type="button" className="btn btn-sm btn-danger" disabled={busy} onClick={() => manage('disconnect')}>Desvincular número</button><button type="button" className="btn btn-sm btn-light" onClick={() => setConfirmUnlink(false)}>Cancelar</button></div>
          </div>}
          {state?.qr && state.qrExpires > Date.now() && <div className="text-center my-3">
            <img src={state.qr} width="240" height="240" alt="QR para vincular este número a Automationia" className="qr-code" />
            <p className="small mt-2 mb-0">En el móvil: WhatsApp → Dispositivos vinculados → Vincular un dispositivo.</p>
          </div>}
          {state?.status === 'qr' && (!state.qr || state.qrExpires <= Date.now()) && <p className="small">{state.canManage ? 'Esperando un nuevo QR…' : 'Un administrador debe escanear el QR para conectar el número.'}</p>}
          <p className="text-muted small mb-0">Adjuntos de hasta 20 MB y transcripción automática de audios cuando esté disponible. Se muestran los mensajes recibidos desde la vinculación.</p>
        </div>}
        {newChatOpen && <form onSubmit={newChat} className="qr-new-chat">
          <label className="form-label small" htmlFor="qr-phone">Nueva conversación</label>
          <div className="d-flex gap-2"><input id="qr-phone" type="tel" autoFocus className="form-control" aria-label="Teléfono con prefijo de país" placeholder="+34 600 000 000" value={phone} onChange={(event) => setPhone(event.target.value)} />
            <button className="btn btn-primary" disabled={!state?.account}>Abrir</button>
          </div>
        </form>}
        <div className="whatsapp-search"><BsSearch aria-hidden="true" /><input aria-label="Buscar conversaciones" placeholder="Buscar por nombre, teléfono o mensaje" value={search} onChange={(event) => setSearch(event.target.value)} />
          {search && <button type="button" className="icon-btn" aria-label="Limpiar búsqueda" onClick={() => setSearch('')}><BsX /></button>}
        </div>
        <div className="whatsapp-chat-list" aria-label="Lista de conversaciones">
          {loading && !chats.length ? <p className="text-muted small p-3" role="status">Cargando conversaciones…</p> : filteredChats.map((chat) => {
            const name = chat.name || phoneLabel(chat.peer);
            return <button type="button" key={chat.peer} className={`whatsapp-chat-item ${peer === chat.peer ? 'active' : ''}`} aria-pressed={peer === chat.peer} onClick={() => selectChat(chat.peer)}>
              <span className="avatar-placeholder" aria-hidden="true">{initials(name)}</span>
              <span className="chat-info"><span className="wa-item-row"><span className="wa-item-name">{name}</span><time className="wa-item-time" dateTime={chat.sent_at}>{listTimeOf(chat.sent_at)}</time></span>
                <span className="wa-item-row"><span className="wa-item-preview">{chat.direction === 'out' ? 'Tú: ' : ''}{chat.body || 'Archivo adjunto'}</span></span>
              </span>
            </button>;
          })}
          {!loading && !filteredChats.length && <div className="qr-list-empty"><BsChatDots aria-hidden="true" /><p>{query ? 'No hay conversaciones que coincidan.' : 'Todavía no hay conversaciones.'}</p>
            {!query && state?.account && <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setNewChatOpen(true)}>Iniciar conversación</button>}
          </div>}
        </div>
      </div>
    </aside>}

    {showChat && <div className="whatsapp-chat">
      {peer ? <>
        <div className="whatsapp-chat-header">
          {isMobile && <button type="button" className="wa-back-btn" aria-label="Volver a conversaciones" onClick={() => { setSidebarOpen(true); setShowEmojis(false); }}><BsChevronLeft /></button>}
          <div className="avatar-placeholder mini" aria-hidden="true">{initials(selectedName)}</div>
          <div className="wa-chat-title"><div className="fw-bold text-truncate">{selectedName}</div><div className="small text-muted text-truncate">{selectedChat?.name ? `${phoneLabel(peer)} · ` : ''}WhatsApp QR</div></div>
          <button type="button" className="btn btn-sm btn-light wa-header-icon" aria-label="Ver conexión de WhatsApp QR" title="Ver conexión de WhatsApp QR" onClick={() => { setConnectionOpen(true); setSidebarOpen(true); }}><BsGear /></button>
        </div>
        {!(isMobile && sidebarOpen) && errorNotice}
        <div className="whatsapp-messages" ref={messagesRef} role="region" aria-label="Mensajes" onScroll={(event) => { const el = event.currentTarget; nearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 140; }}>
          {loading && !messages.length && <p className="text-center text-muted small">Cargando mensajes…</p>}
          {!loading && !messages.length && <p className="text-center text-muted small mt-4">Escribe el primer mensaje de esta conversación.</p>}
          {messages.map((message, index) => <React.Fragment key={message.id}>
            {dayKeyOf(message.sent_at) && (index === 0 || dayKeyOf(message.sent_at) !== dayKeyOf(messages[index - 1].sent_at)) && <div className="wa-day-sep"><span>{dayLabelOf(message.sent_at)}</span></div>}
            <div className={`whatsapp-message ${message.direction === 'out' ? 'out' : 'in'}`}><div className="bubble">
              {message.body && <div className="message-text">{message.body}</div>}
              {message.media_status && <QrAttachment message={message} onError={setError} />}
              {message.transcript_status === 'ready' && <div className="qr-transcript"><strong>Audio transcrito</strong><div>{message.transcript}</div></div>}
              {message.transcript_status === 'pending' && <small className="d-block mt-2 text-muted">Transcripción pendiente…</small>}
              {message.transcript_status === 'unavailable' && <small className="d-block mt-2">No se pudo transcribir este audio.
                {message.media_status === 'ready' && state?.capabilities?.audioTranscription && <button type="button" className="btn btn-sm btn-light ms-2" onClick={async () => {
                  try { await call(`messages/${message.id}/transcribe`, {}); setState((old) => ({ ...old, messages: old.messages.map((item) => item.id === message.id ? { ...item, transcript_status: 'pending' } : item) })); }
                  catch (err) { setError(err.message); }
                }}>Reintentar transcripción</button>}
              </small>}
              <div className="meta text-muted"><time dateTime={message.sent_at} title={message.sent_at ? new Date(message.sent_at).toLocaleString('es-ES') : ''}>{timeOf(message.sent_at)}</time>{message.direction === 'out' && <DeliveryStatus status={message.status} />}</div>
            </div></div>
          </React.Fragment>)}
        </div>
        {state?.status !== 'connected' && <div className="qr-chat-notice" role="status">{connectionLabel}. Podrás enviar mensajes cuando se restablezca la conexión.</div>}
        {file && <div className="qr-pending-file"><BsPaperclip aria-hidden="true" /><span className="text-truncate">{file.name} <small className="text-muted">({(file.size / 1024 / 1024).toLocaleString('es-ES', { maximumFractionDigits: 1 })} MB)</small></span>
          <button type="button" className="icon-btn" aria-label="Quitar archivo" title="Quitar archivo" disabled={busy} onClick={() => setAttachments((old) => ({ ...old, [draftKey]: null }))}><BsX /></button>
        </div>}
        <form onSubmit={send} className="whatsapp-composer">
          <div className="position-relative"><button type="button" className="icon-btn" aria-label="Emojis" title="Emojis" aria-expanded={showEmojis} disabled={busy} onClick={() => setShowEmojis((open) => !open)}><BsEmojiSmile /></button>
            {showEmojis && <div className="emoji-popover qr-emojis"><div className="d-flex align-items-center justify-content-between px-2 pt-2"><span className="small text-muted">Emojis</span><button type="button" className="icon-btn" aria-label="Cerrar emojis" onClick={() => setShowEmojis(false)}><BsX /></button></div><div className="emoji-grid">{FREQUENT_EMOJIS.map((emoji) => <button key={emoji} type="button" className="emoji-btn" aria-label={`Insertar ${emoji}`} onClick={() => insertEmoji(emoji)}>{emoji}</button>)}</div></div>}
          </div>
          <button type="button" className="icon-btn" aria-label="Adjuntar archivo (máximo 20 MB)" title="Adjuntar archivo (máximo 20 MB)" disabled={busy} onClick={() => fileInputRef.current?.click()}><BsPaperclip /></button>
          <input id="qr-attachment" ref={fileInputRef} key={draftKey} type="file" className="d-none" aria-label="Adjuntar archivo (máximo 20 MB)" disabled={busy} onChange={(event) => {
            const chosen = event.target.files?.[0]; event.target.value = '';
            if (chosen && (!chosen.size || chosen.size > MAX_FILE)) { setError('El archivo debe contener entre 1 byte y 20 MB'); return; }
            setAttachments((old) => ({ ...old, [draftKey]: chosen || null }));
          }} />
          <textarea ref={composerRef} className="composer-input" rows="1" maxLength={file ? 1024 : 4096} aria-label="Mensaje" placeholder="Escribe un mensaje…" value={text} disabled={busy} onChange={(event) => setDrafts((old) => ({ ...old, [draftKey]: event.target.value }))} onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing && !isMobile) { event.preventDefault(); send(event); }
          }} />
          <button type="submit" className="icon-btn send-btn" aria-label={busy ? 'Enviando…' : 'Enviar'} title="Enviar" disabled={busy || (!text.trim() && !file) || state?.status !== 'connected'}>{busy ? <BsClock /> : <BsFillSendFill />}</button>
        </form>
      </> : <div className="qr-empty-chat"><BsChatDots aria-hidden="true" /><h3>WhatsApp QR</h3><p>Selecciona una conversación para leer y enviar mensajes.</p>
        {state?.account && <button type="button" className="btn btn-outline-primary" onClick={() => setNewChatOpen(true)}>Nueva conversación</button>}
        <small className="text-muted mt-3">Adjuntos de hasta 20 MB · Transcripción de audios</small>
      </div>}
    </div>}
  </section>;
}
